import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { RentalReportFilterModel } from 'src/app/models/rental/rental-report-filter.model';
import { ClientService } from 'src/app/services/archive/client.service';
import { ConstructionSiteService } from 'src/app/services/archive/construction-site.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { RentalPeriodService } from 'src/app/services/rental/rental-period.service';
import { RentalReportService } from 'src/app/services/rental/rental-report.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
  selector: 'app-construction-without-equipment',
  templateUrl: './construction-without-equipment.component.html',
  styleUrls: ['./construction-without-equipment.component.scss'],
  standalone: false
})
export class ConstructionWithoutEquipmentComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  changeFilters: boolean = false;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  formRentalReportFilter: FormGroup = new FormGroup({});
  listBranch: any[] = [];
  listClient: any[] = [];
  listConstructionSite: any[] = [];
  nameWindow: string = 'Informe de Obras sin Equipo';
  projectName = ProjectName.projectName;

  constructor(private branchService: BranchService,
    private clientService: ClientService,
    private constructionSiteService: ConstructionSiteService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private rentalPeriodService: RentalPeriodService,
    private rentalReportService: RentalReportService,
    private title: Title,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm();
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.getFirstBranchByUserAuthenticatedAsync();
    await this.getAllLists();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  async changeClient(clientId: number) {
    this.formRentalReportFilter.controls["constructionSiteId"].patchValue(0);
    this.changeFilter();
    if (clientId && clientId !== 0) {
      this.listConstructionSite = await firstValueFrom(this.constructionSiteService.getAllConstructionSitesByClientIdForSelectAsync(clientId));
    } else {
      this.listConstructionSite = await firstValueFrom(this.constructionSiteService.getAllConstructionSitesByClientIdForSelectAsync(0));
      this.formRentalReportFilter.controls["clientId"].patchValue(0);
    }
  }

  changeConstructionSite(constructionSiteId: number | null) {
    if (constructionSiteId === 0 || constructionSiteId === null || constructionSiteId === undefined) {
      this.formRentalReportFilter.controls["constructionSiteId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  async clear() {
    this.changeFilter();
    this.validateForm();
    this.getFirstBranchByUserAuthenticatedAsync();
    this.listConstructionSite = await firstValueFrom(this.constructionSiteService.getAllConstructionSitesByClientIdForSelectAsync(0));
  }

  async exportExcel() {
    if (this.changeFilters) {
      const dateInitial = this.formRentalReportFilter.controls['dateInitial'].value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);

      const modelRentalReportFilter = this.formRentalReportFilter.value as RentalReportFilterModel;
      modelRentalReportFilter.year = parseInt(year);
      modelRentalReportFilter.period = parseInt(period);
      try {
        const response = await firstValueFrom(this.rentalReportService.exportRentalReportConstructionWithoutEquipment(modelRentalReportFilter));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Informe de Obras sin Equipo.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
    }
  }

  async getAllLists() {
    try {
      const [branches, clients, constructionSites] = await Promise.all([
        firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
        firstValueFrom(this.clientService.getAllClientsForSelectAsync()),
        firstValueFrom(this.constructionSiteService.getAllConstructionSitesByClientIdForSelectAsync(0))
      ]);
      this.listBranch = branches;
      this.listClient = clients;
      this.listConstructionSite = constructionSites;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas:', error);
      return false;
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const branch = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      this.formRentalReportFilter.controls["branchId"].patchValue(branch.branchId);
      this.getLastRentalPeriod(branch.branchId);
    } catch (error) {
      console.error("Error al consultar la sucursal:", error);
    }
  }

  async getLastRentalPeriod(branchId: number) {
    try {
      const rentalPeriod = await firstValueFrom(this.rentalPeriodService.getLastRentalPeriod(branchId));
      this.formRentalReportFilter.controls["dateInitial"].patchValue(moment(rentalPeriod.initialDate).startOf('month').toDate());
    } catch (error) {
      console.error("Error al consultar el periodo de alquiler:", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.'
      );
    }
  }

  async send() {
    Object.keys(this.formRentalReportFilter.controls).forEach(key => {
      const control = this.formRentalReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formRentalReportFilter.valid) {
      const dateInitial = this.formRentalReportFilter.get("dateInitial")?.value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);

      const modelRentalReportFilter = this.formRentalReportFilter.value as RentalReportFilterModel;
      modelRentalReportFilter.year = parseInt(year);
      modelRentalReportFilter.period = parseInt(period);

      try {
        this.dataReport = await firstValueFrom(this.rentalReportService.rentalReportConstructionWithoutEquipment(modelRentalReportFilter));
        if (this.dataReport.data && this.dataReport.data.length != 0 && this.dataReport.data.length != null) {
          this.changeFilters = true;
        } else {
          this.changeFilters = false;
          this.toastHandlerService.showToast('info', 'No se encontraron datos.');
        }
      } catch (error) {
        console.error("Error al consultar el informe:", error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debe seleccionar una Sucursal válida.'
      },
      dateInitial: {
        required: 'El campo Período es obligatorio.'
      },
      clientId: {
        notNumber: 'Debe seleccionar un Cliente válido.'
      },
      constructionSiteId: {
        notNumber: 'Debe seleccionar una Obra válida.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formRentalReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formRentalReportFilter = this.formBuilder.group({
      branchId: [0, [Validators.required, Validators.min(1)]],
      dateInitial: ['', [Validators.required]],
      clientId: [0, [CustomValidators.numberOnlyValidator()]],
      constructionSiteId: [0, [CustomValidators.numberOnlyValidator()]],
    });
  }

}
